import context, { Constants } from '@truckdown/systems';
import { INavItem } from '@truckdown/components-systems';
import { defineComponent, Ref, ref, onMounted, computed } from 'vue';
import { AccountSummaryModel, AccountType } from "@truckdown/accounts";

const alwaysDisplay = function () {
    return true;
}

const csrOrAdminOnly = function () {
    return context.isInRole(Constants.Roles.SystemRole) || context.isInRole(Constants.Roles.CSRRole);
}

const getSubItem = function (name: string, icon: string, url: string, display?: () => boolean, tooltip?: string, id?: string): ISelectableItem {
    return {
        id: id ?? name,
        name: name,
        icon: icon,
        url: url,
        tooltip: tooltip,
        active: false,
        display: display ?? alwaysDisplay,
        subItems: []
    };
}

const getAccountManagement = function (account: AccountSummaryModel) {
    return [
        getSubItem('Account Details', 'faCircleInfo', '/accounts/admin/' + account.ain),
        getSubItem('Members', 'faPeopleGroup', '/accounts/admin/members/' + account.ain),
        getSubItem('Duplicate Accounts', 'faCopy', '/accounts/admin/duplicate-accounts/' + account.ain),
        getSubItem('Billing', 'faCreditCard', '/billing/' + account.ain),
        getSubItem('Users', 'faUsers', '/accounts/admin/users/' + encodeURIComponent(account.ain)),
        getSubItem('Integrations', 'faKey', '/accounts/admin/integrations/' + encodeURIComponent(account.ain))
    ]
}

const getAdvertising = function (account: AccountSummaryModel) {
    return [
        getSubItem('Listings', 'faLocationDot', '/listings/admin/' + account.ain),
        getSubItem('Service Requests', 'faScrewdriverWrench', '/servicerequests/vendor/' + account.ain, alwaysDisplay, undefined, 'Vendor Requests'),
        getSubItem('Promotions', 'faMoneyBill', '/promotions/admin/' + account.ain),
        getSubItem('Banner Ads', 'faRectangleAd', '/accounts-admin/view-account/banners/' + encodeURIComponent(account.id)),
        getSubItem('Ratings', 'faFaceSmile', '/accounts-admin/view-account/vendor-ratings/' + encodeURIComponent(account.id), alwaysDisplay, undefined, 'Vendor Ratings'),
        getSubItem('Analytics', 'faChartLine', '/analytics/admin/va/' + encodeURIComponent(account.id), alwaysDisplay, undefined, 'Vendor Analytics'),
        getSubItem('Coverage Analysis', 'faGlobe', '/lsearch/analyze-coverage/' + encodeURIComponent(account.id))
    ]
}

const getFleet = function (account: AccountSummaryModel) {
    return [
        //TODO: history admin: getSubItem('Search & Call History', 'faHistory', '/search/call-history'),
        getSubItem('Service Requests', 'faScrewdriverWrench', '/servicerequests/fleet/' + account.ain, alwaysDisplay, undefined, 'Fleet Requests'),
        getSubItem('Ratings', 'faFaceSmile', '/accounts-admin/view-account/user-ratings/' + encodeURIComponent(account.id), alwaysDisplay, undefined, 'User Ratings'),
        getSubItem('Favorite Vendors', 'faThumbsUp', '/accounts-admin/view-account/servicenetwork/' + encodeURIComponent(account.id) + '?Filter=preferred'),
        getSubItem('Avoided Vendors', 'faThumbsDown', '/accounts-admin/view-account/servicenetwork/' + encodeURIComponent(account.id) + '?Filter=donotuse'),
        getSubItem('Routing', 'faRoute', '/accounts-admin/view-account/routes/' + encodeURIComponent(account.id)),
        getSubItem('Fuel Receipts', 'faDroplet', '/accounts-admin/view-account/receipts/' + encodeURIComponent(account.id)),
        getSubItem('Analytics', 'faChartLine', '/analytics/admin/a/' + encodeURIComponent(account.id), alwaysDisplay, undefined, 'User Analytics')
    ]
}

const getTools = function (account: AccountSummaryModel) {
    return [
        getSubItem('Tasks', 'faListCheck', '/accounts-admin/view-account/tasks/' + encodeURIComponent(account.id)),
        getSubItem('Emails', 'faEnvelope', '/accounts-admin/view-account/emails/' + encodeURIComponent(account.id)),
        getSubItem('Marketing Lists', 'faBullseye', '/accounts-admin/view-account/lists/' + encodeURIComponent(account.id)),
        getSubItem('Marketing Contacts', 'faAddressBook', '/accounts-admin/view-account/accountContact/' + encodeURIComponent(account.id)),
        getSubItem('Reporting', 'faFileLines', '/accounts-admin/view-account/reporting/' + encodeURIComponent(account.id))
    ]
}

const getItems = function (account: AccountSummaryModel) {
    return [
        {
            name: 'Account Management',
            icon: 'faUserGear',
            active: false,
            subItems: getAccountManagement(account),
            display: alwaysDisplay
        },
        {
            name: 'Advertising Services',
            icon: 'faBriefcase',
            active: false,
            subItems: getAdvertising(account),
            display: alwaysDisplay
        },
        {
            name: 'Fleet / Driver Services',
            icon: 'faTruckFront',
            active: false,
            subItems: getFleet(account),
            display: alwaysDisplay
        },
        {
            name: 'Tools',
            icon: 'faGears',
            active: false,
            subItems: getTools(account),
            display: alwaysDisplay
        }
    ];
};

interface IItem {
    id?: string;
    name: string;
    tooltip?: string;
    icon: string;
    active: boolean;
    subItems: ISelectableItem[];
    display: () => boolean;
}

interface ISelectableItem extends INavItem {
    id: string;
    subItems: ISelectableItem[];
    active: boolean;
}

interface IData {
    hideNav: Ref<boolean>,
    items: Ref<IItem[]>,
    shownSubMenus: Ref<string[]>
};

interface IProps {
    openSection: string,
    activeItem: string,
    subSection: string,
    subItems: INavItem[],
    accountSummary: AccountSummaryModel
}

const getSection = function (name: string, items: IItem[]): IItem | ISelectableItem | null {
    for (let i = 0; i < items.length; i++) {
        var itm = items[i];
        if (itm.id == name || itm.name == name) {
            return itm;
        }

        for (let i2 = 0; i2 < itm.subItems.length; i2++) {
            var subItm = itm.subItems[i2];

            if (subItm) {
                if (subItm.id == name) {
                    return subItm;
                }

                for (let i3 = 0; i3 < subItm.subItems.length; i3++) {
                    var final = subItm.subItems[i3];

                    if (final.id == name) {
                        return final;
                    }
                }
            }
        }
    }
    return null;
}

const setMenuOptions = function (props: IProps, items: IItem[]) {

    if (props.subSection.length > 0 && props.subItems.length > 0) {
        let section = getSection(props.subSection, items);
        if (section != null) {
            section.subItems = props.subItems.map((itm) => {
                return {
                    id: itm.id ?? itm.name,
                    name: itm.name,
                    tooltip: itm.tooltip,
                    icon: itm.icon,
                    url: itm.url,
                    display: alwaysDisplay,
                    active: false,
                    subItems: []
                } as ISelectableItem
            });
        }
    }

    if (props.activeItem.length > 0) {
        let activeSection = getSection(props.activeItem, items);
        if (activeSection != null) {
            activeSection.active = true;
        }
    }
}

const getNavHideStatus = function () {
    var queryParams = new URLSearchParams(window.location.search);
    if (queryParams.has('hsn')) {
        return true;
    }

    return window.innerWidth < 525;
}

const getShownMenus = function (props: IProps) {
    var start = csrOrAdminOnly() ? [] : [
        'Account Management',
        props.accountSummary.accountType == AccountType.Vendor ? 'Advertising Services' : 'Fleet / Driver Services'
    ];

    if (props.openSection.length > 0 && !start.some(s => s == props.openSection)) {
        start.push(props.openSection);
    }

    return start;
}

const getData = function (props: IProps): IData {
    var items = getItems(props.accountSummary);
    let shownMenus = getShownMenus(props);
    setMenuOptions(props, items);

    return {
        hideNav: ref<boolean>(getNavHideStatus()),
        items: ref<IItem[]>(items),
        shownSubMenus: ref<string[]>(shownMenus)
    }
}

const getMethods = function (data: IData) {
    return {
        toggleNav: function (ev: Event) {
            data.hideNav.value = !data.hideNav.value;
            var queryParams = new URLSearchParams(window.location.search);
            if (data.hideNav.value) {
                queryParams.set('hsn', '1');
                history.replaceState(null, '', '?' + queryParams.toString());
            } else {
                if (queryParams.has('hsn')) {
                    queryParams.delete('hsn');
                    var newSearch = queryParams.toString();
                    if (newSearch.length == 0) {
                        history.replaceState(null, '', location.pathname);
                    } else {
                        history.replaceState(null, '', '?' + queryParams.toString());
                    }
                }
            }
        },
        toggleMenu: function (name: string) {
            if (data.shownSubMenus.value.some(v => v == name)) {
                data.shownSubMenus.value = data.shownSubMenus.value.filter(v => v != name);
                return true;
            } else {
                data.shownSubMenus.value.push(name);
            }
        },
        shouldShowMenu: function (name: string) {
            if (data.shownSubMenus.value.some(v => v == name)) {
                return true;
            }
            return false;
        },
        getUrl: function (url: string) {
            if (data.hideNav.value) {
                var hasQuery = url.indexOf('?') > 0;
                if (hasQuery) {
                    return url + '&hsn=1';
                } else {
                    return url + '?hsn=1';
                }
            }

            return url;
        }
    };
}

export default defineComponent({
    name: 'AdminSideNav',
    props: {
        openSection: {
            type: String,
            default: ''
        },
        activeItem: {
            type: String,
            default: ''
        },
        subSection: {
            type: String,
            default: ''
        },
        subItems: {
            type: Array as () => INavItem[],
            default: []
        },
        accountSummary: {
            type: Object as () => AccountSummaryModel,
            required: true
        },
    },
    setup: function (props) {
        let data = getData(props as IProps);

        let methods = getMethods(data);

        return {
            ...data,
            ...methods
        };
    }
});
